import h from 'hyperscript';
import downloadIcon from '@disy/cadenza-icons/export.svg';
import more from '@disy/cadenza-icons/more.svg';
import reportIcon from '@disy/cadenza-icons/report.svg';

import { createIconButton } from 'ui/button/button';
import { setupMenuButton } from 'ui/menu/menu';
import { BEFORE_LOAD_DATA, ERROR_LOAD_DATA, LOAD_DATA } from 'ui/data-loading-event-types';

import { logger } from 'cadenza/utils/logging';
import { getReportConfig, getReportDownloadUrl, getReportFilename } from 'cadenza/api-client/report-api';
import { EXECUTION_CONFIRMED, PARAMETER_CONDITION_HASH, PARAMETER_MIMETYPE } from 'cadenza/api-client/api-constants';
import { MIMETYPE_PDF } from 'cadenza/model/media-type';
import { getSubnav } from 'cadenza/utils/classic-dependency-registry';
import { ScheduleJobAction } from 'cadenza/actions/schedule-job-action';
import { ManageJobAction } from 'cadenza/actions/manage-job-action';
import { icon } from 'cadenza/utils/icon/icon';
import { CONDITIONS_HIERARCHIES_CHANGE } from 'cadenza/conditions/event-types';
import { on, unByKey } from 'cadenza/utils/event-util';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { BackendAbortController } from 'cadenza/api-client/abort-controller/backend-abort-controller';
import {
  displayOnDemandViewWithStateCanceled,
  getOnDemandViewElement,
  setOnDemandViewStateForFinishedLoading
} from 'cadenza/on-demand-view/on-demand-view';
import { SingleExecutionHelper } from 'cadenza/api-client/abort-controller/single-execution-helper';
import { ReportExportAction } from 'cadenza/report/report-export-action';
import { getUrlParam, setParam } from 'cadenza/utils/url-param-utils';
import { EXPORT_BUTTON_STYLE_CLASS } from 'cadenza/header/subnav-constants';

import './report.css';
import i18n from './report.properties';

const AbortReason = {
  CONDITION_CHANGE: 'CONDITION_CHANGE',
  MANUAL: 'MANUAL'
};

class Report extends HTMLElement {

  #alerts;
  #config;
  #loadReportSingleExecutionHelper = new SingleExecutionHelper();
  #abortReason;

  #downloadButton;
  #reportSchedulerButton;

  connectedCallback () {
    this.#config = this.previousElementSibling.value;
    this.#alerts = this.parentElement.querySelector('d-alerts');
    const eventSource = document.querySelector('.d-filter-controller')
      || this.closest('.on-demand-parent').querySelector('.d-on-demand-view');
    this._eventKeys = [ on(eventSource, CONDITIONS_HIERARCHIES_CHANGE, event => this._onConditionOrHierarchyChanged(event)) ];
    const selectorResultCount = document.querySelector('.d-selector-result-count');
    if (selectorResultCount) {
      selectorResultCount.setValues(this.#config.recordsTotal, this.#config.uniqueRecordsTotal);
      this._eventKeys.push(
        on(this, LOAD_DATA, event => selectorResultCount.setValues(event.detail.recordsTotal, event.detail.uniqueRecordsTotal)),
        on(this, BEFORE_LOAD_DATA, () => selectorResultCount.setLoading()),
        on(this, ERROR_LOAD_DATA, () => selectorResultCount.hideLoading()));
    }
    setTimeout(() => {
      const onDemandView = getOnDemandViewElement();
      if (!onDemandView || onDemandView.hidden) {
        const abortController = new BackendAbortController();
        abortController.abortOnUnload = true;
        this._updateVisibility();
        this._getLoadingSpinner().setCancelHandler(() => {
          this.#abortReason = AbortReason.MANUAL;
          abortController.abort();
        });
        if (!this.hidden) {
          const conditionValuesSetHash = getUrlParam({ name: PARAMETER_CONDITION_HASH });
          this._showLoadingSpinner();
          this._loadReport(abortController, conditionValuesSetHash)
            .finally(() => this._hideLoadingSpinner());
        }
      }
    });
    this._addReportSchedulerButtonToSubnav();
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
    this._removeSubnavButtons();
  }

  _getLoadingSpinner () {
    return this.parentElement.querySelector('d-progress-spinner-overlay');
  }

  _hideLoadingSpinner () {
    const loadingSpinner = this._getLoadingSpinner();
    loadingSpinner.setAttribute('waitingCount',
      Number(loadingSpinner.getAttribute('waitingCount') || 0) - 1);
    if (Number(loadingSpinner.getAttribute('waitingCount')) < 1) {
      loadingSpinner.hidden = true;
    }
  }

  /**
   * Utility for aborting. The wait count is decremented once the
   * promise that waits for the aborted request finishes.
   */
  _hideLoadingSpinnerIfAtMostOneIsWaiting () {
    const loadingSpinner = this._getLoadingSpinner();
    if (Number(loadingSpinner.getAttribute('waitingCount')) < 2) {
      loadingSpinner.hidden = true;
    }
  }

  _showLoadingSpinner () {
    const loadingSpinner = this._getLoadingSpinner();
    loadingSpinner.setAttribute('waitingCount',
      1 + Number(loadingSpinner.getAttribute('waitingCount') || 0));
    if (Number(loadingSpinner.getAttribute('waitingCount')) > 0) {
      loadingSpinner.hidden = false;
    }
  }

  _abort () {
    this.#abortReason = AbortReason.CONDITION_CHANGE;
    this.#loadReportSingleExecutionHelper.cancelCurrent();
    this._hideLoadingSpinnerIfAtMostOneIsWaiting();
  }

  _updateVisibility () {
    const showAlert = this.#config.resultTruncated;
    const showContent = this.#config.hasResult && !this.#config.resultTruncated;
    const showNoResult = !this.#config.hasResult && !this.#config.hasInsufficientRepositoryConfig;
    const noResult = this.parentElement.querySelector('.noResult');
    this.#alerts.hidden = !showAlert;
    this.hidden = !showContent;
    noResult.hidden = !showNoResult;
    if (showAlert) {
      this._showAlert(
        i18n('selector.truncatedErrorTitle'),
        i18n('selector.truncatedErrorText', { resultCount: this.#config.recordsTotal })
      );
    } else {
      this.#alerts.alerts = [];
    }
  }

  _showAlert (title, message) {
    this.#alerts.hidden = false;
    this.#alerts.alerts = [
      {
        type: 'error',
        title,
        message
      }
    ];
  }

  _loadReport (abortController, conditionValuesSetHash) {
    this._removeDownloadSubnavButton();
    const onDemandView = getOnDemandViewElement();
    onDemandView.hidden = true;
    onDemandView.showHiddenElement();
    return this._loadReportInternal(this.defaultFormat, abortController).then(generatedReportMetadata => {
      if (generatedReportMetadata) {
        const preGeneratedDefaultFormatReportUrl = getReportDownloadUrl(
          generatedReportMetadata.serverFileRef,
          generatedReportMetadata.downloadFileName,
          this.defaultFormat.mimeType);
        this._addDownloadButtonToSubnav(preGeneratedDefaultFormatReportUrl);
        // Create content not before having the report download url:
        // MS Edge has a weird bug with the <object>-Tag , look at CADENZA-9666
        this._createUiContent(preGeneratedDefaultFormatReportUrl);
      } else {
        onDemandView.hidden = false;
        displayOnDemandViewWithStateCanceled(() => this._refresh(), conditionValuesSetHash);
      }
    }).catch(error => {
      if (!abortController.signal.aborted) {
        logger.error('Pre-loading report in default format failed', error);
        this._showAlert(i18n('unknownError.title'), i18n('unknownError.message'));
      }
    });
  }

  _removeDownloadSubnavButton () {
    this.#downloadButton?.remove();
  }

  _removeSubnavButtons () {
    this._removeDownloadSubnavButton();
    this.#reportSchedulerButton?.remove();
  }

  /**
   * Generates the report in the passed format and returns its filename, which can be used to build a download URL.
   *
   * @param format - media type to generate the report in
   * @param abortController
   * @return promise that resolves with the filename of the generated report or with null if the cancel button was
   *   clicked during the report generation
   * @private
   */
  _loadReportInternal (format, abortController) {
    this.#loadReportSingleExecutionHelper.submit(abortController);
    this.#abortReason = AbortReason.CONDITION_CHANGE;

    return getReportFilename(format.url, abortController.signal)
      .then(generatedReportMetadata => {
        return generatedReportMetadata;
      })
      .catch(error => {
        if (error.key === 'result-limits-exception') {
          this.#alerts.alerts = [ {
            type: 'info',
            message: error.detail
          } ];
          this.#alerts.hidden = false;
          this.hidden = true;
        } else if (error.key === 'result-empty-condition-exception') {
          this.#alerts.alerts = [ {
            type: 'info',
            message: i18n('common:noResult')
          } ];
          this.#alerts.hidden = false;
          this.hidden = true;
        } else if (error.key === 'insufficient-repository-configuration') {
          this.#alerts.alerts = [ {
            type: 'warning',
            message: error.detail
          } ];
          this.#alerts.hidden = false;
          this.hidden = true;
        } else if (this.#abortReason === AbortReason.MANUAL) {
          return null;
        } else {
          logger.error('Failed to show report as processing result');
        }
        this.dispatchEvent(new CustomEvent(ERROR_LOAD_DATA, { detail: error }));
        throw error;
      }
      );
  }

  _addDownloadButtonToSubnav (preGeneratedDefaultFormatReportUrl) {
    const exportActions = [];
    for (const format of this.formats) {
      const isDefaultFormat = format === this.defaultFormat;
      const url = isDefaultFormat ? preGeneratedDefaultFormatReportUrl : cadenzaUrl(format.url);
      let label = i18n(`export.label.${format.defaultExtension}`);
      if (label.startsWith('???')) {
        label = i18n('export.label.general', { extension: format.defaultExtension.toUpperCase() });
      }
      exportActions.push(new ReportExportAction(format.mimeType, url, label));
    }
    const button = createIconButton(icon(downloadIcon), i18n('export.title'), {
      styleClass: EXPORT_BUTTON_STYLE_CLASS,
      variant: 'borderless'
    });
    setupMenuButton(button, exportActions);
    getSubnav()?.addButton(button); // CADENZA-35173 In case of using CustomFeatureInfo or URL API there's maybe no subnav in the DOM and that's correct.
    this.#downloadButton = button;
  }

  _addReportSchedulerButtonToSubnav () {
    const subnav = getSubnav();
    if (subnav) {
      const scheduleJobAction = new ScheduleJobAction();
      const manageJobAction = new ManageJobAction();
      const supportedButtons = [];
      if (scheduleJobAction.available) {
        supportedButtons.push(scheduleJobAction);
      }
      if (manageJobAction.available) {
        supportedButtons.push(manageJobAction);
      }

      if (supportedButtons.length > 0) {
        const reportSchedulerButton = h('button.button.button-borderless', icon(more));
        setupMenuButton(reportSchedulerButton, () => supportedButtons);
        subnav.setCenterControls([ reportSchedulerButton ]);
        this.#reportSchedulerButton = reportSchedulerButton;
      }
    }
  }

  _onConditionOrHierarchyChanged (event) {
    if (event.defaultPrevented) {
      this._abort();
      return;
    }
    // when called from a round-trip over the on-demand-view the old conditionValuesSetHash is needed to avoid canceling the wrong request.
    this._refresh(event.detail?.oldConditionValuesSetHash);
  }

  _wasExecutionConfirmed () {
    const param = new URLSearchParams(window.location.search);
    const executionConfirmed = param.get(EXECUTION_CONFIRMED);
    return executionConfirmed != null && executionConfirmed === 'true';
  }

  _isOnDemandExecution () {
    const onDemandView = getOnDemandViewElement();
    return onDemandView && !onDemandView.hidden;
  }

  _refresh (oldConditionValuesSetHash = null) {
    // do not refresh below on demand overlay
    const onDemandView = getOnDemandViewElement();
    if (onDemandView && !onDemandView.hidden && !this._wasExecutionConfirmed()) {
      return () => null;
    }
    this.dispatchEvent(new CustomEvent(BEFORE_LOAD_DATA));
    const abortController = new BackendAbortController();
    abortController.abortOnUnload = true;
    this._getLoadingSpinner().setCancelHandler(() => {
      this.#abortReason = AbortReason.MANUAL;
      abortController.abort();
    });
    const conditionValuesSetHash = oldConditionValuesSetHash || getUrlParam({ name: PARAMETER_CONDITION_HASH });
    this._showLoadingSpinner();
    return getReportConfig()
      .then(config => {
        this.#config = config;
        this.dispatchEvent(new CustomEvent(LOAD_DATA, { detail: config }));
        this._updateVisibility();
        if (!this.hidden) {
          this._loadReport(abortController, conditionValuesSetHash)
            .finally(() => this._hideLoadingSpinner());
        } else {
          setOnDemandViewStateForFinishedLoading();
          this._hideLoadingSpinner();
        }
      })
      .catch(err => {
        this._hideLoadingSpinner();
        this.dispatchEvent(new CustomEvent(ERROR_LOAD_DATA, { detail: err }));
        if (err.key === 'result-limits-exception' || err.key === 'insufficient-repository-configuration') {
          setOnDemandViewStateForFinishedLoading();
          this.#alerts.alerts = [ {
            type: 'warning',
            message: err.detail
          } ];
          this.#alerts.hidden = false;
          this.hidden = true;
        } else {
          logger.error('Error retrieving reportConfig', err);
        }
      });
  }

  _createUiContent (preGeneratedDefaultFormatReportUrl) {
    this.innerHTML = '';
    this.append(h('div.d-report--download-outer-container', h('object#reportObject', {
      type: this.defaultFormat.mimeType,
      data: preGeneratedDefaultFormatReportUrl,
      width: '100%',
      height: '100%'
    }, [
      h('param', { name: 'wmode', value: 'opaque' }),
      ...this._createNoInlineContent(preGeneratedDefaultFormatReportUrl)
    ])));
  }

  /**
   * Creates download links that become visible when the report cannot be displayed inline in its default format.
   *
   * @param preGeneratedDefaultFormatReportUrl - URL of pre-generated report in the default render format
   * @return download links for each configured format of the report
   */
  _createNoInlineContent (preGeneratedDefaultFormatReportUrl) {
    const header = h('h1.d-report--download-title', i18n('downloadTitle'));
    const text = h('p.d-report--download-text', i18n('downloadText'));
    const downloadButtonsContainer = h('div.d-report--download-buttons-container');
    for (const format of this.formats) {
      const downloadButton = this._createDownloadButton(format, preGeneratedDefaultFormatReportUrl);
      downloadButtonsContainer.append(downloadButton);
    }
    const downloadContainer = h('div.d-report--download-inner-container', [ header, text, downloadButtonsContainer ]);
    return [ downloadContainer ];
  }

  _createDownloadButton (format, preGeneratedDefaultFormatReportUrl) {
    const isDefaultFormat = format === this.defaultFormat;
    const islinkToInline = !isDefaultFormat && format.mimeType === MIMETYPE_PDF;

    const downloadIcon = icon(reportIcon, { styleClass: 'd-report--download-icon' });
    const downloadText = h('div.d-report--download-label', format.defaultExtension);
    const downloadLink = h('a.d-report--download-link#popupLinkToReport', {
      name: 'popupLinkToReport'
    }, downloadIcon, downloadText);

    if (isDefaultFormat) {
      downloadLink.classList.add('d-report--download-link-default-format');
    }

    let clickAndKeyHandler;
    const downloadUrl = isDefaultFormat ? preGeneratedDefaultFormatReportUrl : cadenzaUrl(format.url);
    if (islinkToInline) {
      clickAndKeyHandler = _ => {
        setParam({ paramName: PARAMETER_MIMETYPE, paramValue: format.mimeType });
        this._recreateObjectTagWithFormat(format);
        // Fallback for when the inline view does not work. Has to be in setTimeout. Otherwise some browsers (e.g. Edge)
        // follow the link already on the first click which is supposed to open the inline view and not trigger a download.
        setTimeout(() => { downloadLink.href = downloadUrl; });
      };
      downloadLink.href = 'javascript:void(0)';
    } else {
      clickAndKeyHandler = _ => {
        setParam({ paramName: PARAMETER_MIMETYPE, paramValue: format.mimeType });
      };
      downloadLink.href = downloadUrl;
      downloadLink.target = '_blank';
      downloadLink.rel = 'noopener';
    }

    downloadLink.onclick = clickAndKeyHandler;
    downloadLink.onkeypress = event => {
      if (event.key === 'Enter' || event.key === 'Space') {
        clickAndKeyHandler();
      }
    };

    return downloadLink;
  }

  _recreateObjectTagWithFormat (format) {
    const abortController = new BackendAbortController();
    abortController.abortOnUnload = true;
    this._showLoadingSpinner();
    this._loadReportInternal(format, abortController).then(generatedReportMetadata => {
      if (generatedReportMetadata) {
        const inlineReportUrl = getReportDownloadUrl(
          generatedReportMetadata.serverFileRef,
          generatedReportMetadata.downloadFileName,
          MIMETYPE_PDF);
        const objectTag = document.querySelector('object#reportObject');
        objectTag.type = MIMETYPE_PDF;
        objectTag.data = inlineReportUrl;

        const parent = objectTag.parentElement;
        const clone = objectTag.cloneNode(true);
        objectTag.remove();
        this._hideLoadingSpinner();
        parent.append(clone);
      }
    }).catch(error => logger.error('Loading report for link to inline view failed', error));
  }

  get defaultFormat () {
    const mediaType = getUrlParam({ name: PARAMETER_MIMETYPE });
    if (mediaType) {
      for (const format of this.#config.renderFormats) {
        if (mediaType === format.mimeType) {
          return format;
        }
      }
    }
    return this.#config.renderFormats[0];
  }

  get formats () {
    return this.#config.renderFormats;
  }

  get printName () {
    return this.#config.title;
  }

}

customElements.define('d-report', Report);
